import {
    homeNode,
    workflowRepository,
} from "motw_core/Repositories/WorkflowRepository";
import { WithAnimatedProgressBarAsync } from "source/app/presentation/Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import { RefreshVisibleWorkflowNodesAsync } from "source/app/presentation/MainPanel/Workflows/Helpers/RefreshVisibleWorkflowNodes";
import { GetWorkflowsState } from "source/app/presentation/MainPanel/Workflows/Workflows";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import { DispatchEvent } from "source/app/services/Events/Events";
import { EEventType } from "source/app/services/Events/EEventType";
import { EPanelId } from "source/app/services/Shared/EPanelId";
import { LogWorkflowNavigation } from "source/app/services/Workflows/WorkflowNavigationDiagnostics";
import * as toastr from "toastr";
import { IRoutingInfo } from "./IRoutingInfo";

export async function RouteWorkflowsAsync(routingInfo: IRoutingInfo) {
    const targetPanelName = routingInfo.panelName;

    LogWorkflowNavigation("RouteWorkflows:entry", {
        panelName: routingInfo.panelName,
        elementId: routingInfo.elementId,
    });

    if (targetPanelName !== EPanelId[EPanelId.Workflows]) {
        LogWorkflowNavigation("RouteWorkflows:skip-non-workflows", {
            panelName: routingInfo.panelName,
        });
        return;
    }

    const isReActivatingWorkflowsPanel = !routingInfo.elementId;

    if (isReActivatingWorkflowsPanel) {
        LogWorkflowNavigation("RouteWorkflows:skip-reactivating-panel");
        return;
    }

    const workflows = GetWorkflowsState();

    if (!workflows) {
        LogWorkflowNavigation("RouteWorkflows:skip-no-workflows-state");
        DispatchEvent(EEventType.Workflows_RootNodeChange_FinishLoading);
        return;
    }

    const targetRootNodeId = GetTargetRootNodeId(routingInfo);
    LogWorkflowNavigation("RouteWorkflows:target", { targetRootNodeId });

    let expectedRootNode;

    try {
        LogWorkflowNavigation("RouteWorkflows:get-node-start", {
            targetRootNodeId,
        });

        expectedRootNode =
            targetRootNodeId === homeNode.id
                ? homeNode
                : await WithAnimatedProgressBarAsync(
                      () =>
                          workflowRepository.GetNodeById(
                              ClientDataProvider,
                              targetRootNodeId
                          ),
                      `RouteWorkflows_GetNodeById`
                  );

        LogWorkflowNavigation("RouteWorkflows:get-node-finish", {
            targetRootNodeId,
            foundNode: !!expectedRootNode,
        });
    } catch (e) {
        LogWorkflowNavigation("RouteWorkflows:get-node-error", {
            targetRootNodeId,
            error: GetErrorMessage(e),
        });
        DispatchEvent(EEventType.Workflows_RootNodeChange_FinishLoading);
        throw e;
    }

    if (!expectedRootNode) {
        LogWorkflowNavigation("RouteWorkflows:not-found", {
            targetRootNodeId,
        });
        toastr.error(`Could not find node with id '${targetRootNodeId}'`);
        DispatchEvent(EEventType.Workflows_RootNodeChange_FinishLoading);
        return;
    }

    try {
        LogWorkflowNavigation("RouteWorkflows:set-root", {
            targetRootNodeId,
        });
        workflows.setState({ rootNode: expectedRootNode });
        document.title = expectedRootNode.name;
        LogWorkflowNavigation("RouteWorkflows:refresh-visible-start", {
            targetRootNodeId,
        });
        await RefreshVisibleWorkflowNodesAsync(expectedRootNode);
        LogWorkflowNavigation("RouteWorkflows:refresh-visible-finish", {
            targetRootNodeId,
        });
    } catch (e) {
        LogWorkflowNavigation("RouteWorkflows:refresh-visible-error", {
            targetRootNodeId,
            error: GetErrorMessage(e),
        });
        DispatchEvent(EEventType.Workflows_RootNodeChange_FinishLoading);
        throw e;
    }
}

function GetTargetRootNodeId(routingInfo: IRoutingInfo) {
    let targetElementIdString = routingInfo.elementId;

    if (!targetElementIdString) {
        targetElementIdString = `${homeNode.id}`;
    }

    const targetRootNodeId = Number.parseInt(targetElementIdString);
    return targetRootNodeId;
}

function GetErrorMessage(e: unknown) {
    return e instanceof Error ? e.message : String(e);
}
